import { useState, useEffect, FormEvent } from 'react';
import { api } from '../lib/api';
import { secopsApi } from '../lib/secops-api';
import { useSecurityConfig } from '../hooks/useSecurity';

type TabId = 'platforms' | 'team' | 'apikeys' | 'github' | 'notifications' | 'scans' | 'audit';

interface Platform {
  id: string;
  name: string;
  github_repo: string;
  criticality: string;
  target_url?: string;
  domains?: string[];
  status: string;
}

interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  status: 'active' | 'invited';
  joined_at?: string;
}

interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  created_at: string;
  last_used_at?: string;
  expires_at?: string;
}

interface NotificationRoutingRule {
  id: string;
  type: string;
  tenant_id: string;
  name: string;
  enabled: boolean;
  conditions: {
    severity: string[];
    categories: string[];
    sources: string[];
  };
  actions: {
    email: { enabled: boolean; recipients: string[] };
    slack: { enabled: boolean; webhook_url: string; channel: string };
    psa_ticket: { enabled: boolean; board_id: string; priority: string };
    webhook: { enabled: boolean; url: string };
  };
  created_at: string;
  updated_at: string;
}

interface ScanSchedule {
  id: string;
  type: string;
  tenant_id: string;
  name: string;
  enabled: boolean;
  scan_type: string;
  targets: string[];
  frequency: string;
  cron_expression: string;
  day_of_week: number | null;
  day_of_month: number | null;
  time_utc: string;
  last_run_at: string | null;
  next_run_at: string | null;
  notification_on_complete: boolean;
  notification_on_findings: boolean;
  created_at: string;
  updated_at: string;
}

const TABS: { id: TabId; label: string }[] = [
  { id: 'platforms', label: 'Platforms' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'scans', label: 'Scans' },
  { id: 'team', label: 'Team' },
  { id: 'apikeys', label: 'API Keys' },
  { id: 'audit', label: 'Audit Log' },
  { id: 'github', label: 'GitHub' },
];

const CRITICALITY_OPTIONS = ['critical', 'high', 'medium', 'low'];
const ROLE_OPTIONS = ['admin', 'member', 'viewer'];

function useList<T>(initialUrl: string) {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  async function load() {
    if (loaded) return;
    setLoading(true);
    try {
      const data = await api.get<T[]>(initialUrl);
      setItems(Array.isArray(data) ? data : []);
      setLoaded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  return { items, setItems, loading, error, load };
}

// ---- Platforms Tab ----
function PlatformsTab() {
  const { items: platforms, setItems, loading, error, load } = useList<Platform>('/platforms');
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', github_repo: '', criticality: 'medium', target_url: '', domains: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useState(() => { load(); });

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setSaving(true);
    try {
      const domainsArr = form.domains.split(',').map(d => d.trim()).filter(Boolean);
      const created = await api.post<Platform>('/platforms', { ...form, domains: domainsArr });
      setItems(prev => [...prev, created]);
      setShowAdd(false);
      setForm({ name: '', github_repo: '', criticality: 'medium', target_url: '', domains: '' });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to add platform');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(id: string) {
    if (!confirm('Remove this platform? All associated data will be archived.')) return;
    await api.delete(`/platforms/${id}`);
    setItems(prev => prev.filter(p => p.id !== id));
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{platforms.length} platform{platforms.length !== 1 ? 's' : ''} connected</p>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Platform
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">{error}</div>}

      {showAdd && (
        <div className="border border-gray-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Add New Platform</h3>
          {formError && <div className="mb-3 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{formError}</div>}
          <form onSubmit={handleAdd} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
              <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" placeholder="My App" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">GitHub Repo *</label>
              <input required value={form.github_repo} onChange={e => setForm(f => ({ ...f, github_repo: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" placeholder="owner/repo" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Criticality</label>
              <select value={form.criticality} onChange={e => setForm(f => ({ ...f, criticality: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
                {CRITICALITY_OPTIONS.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Target URL</label>
              <input type="url" value={form.target_url} onChange={e => setForm(f => ({ ...f, target_url: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" placeholder="https://..." />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Domains (comma-separated)</label>
              <input value={form.domains} onChange={e => setForm(f => ({ ...f, domains: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" placeholder="example.com, api.example.com" />
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
              <button type="submit" disabled={saving} className="bg-indigo-600 text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
                {saving ? 'Adding...' : 'Add Platform'}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="animate-pulse space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-gray-100 rounded-lg" />)}</div>
      ) : platforms.length === 0 ? (
        <div className="text-center py-10 text-gray-400 text-sm">No platforms yet. Click "Add Platform" to get started.</div>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
          {platforms.map(p => (
            <div key={p.id} className="px-5 py-4 flex items-center justify-between hover:bg-gray-50">
              <div>
                <p className="text-sm font-medium text-gray-900">{p.name}</p>
                <p className="text-xs text-gray-400 mt-0.5">{p.github_repo} · <span className="capitalize">{p.criticality}</span></p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${p.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {p.status}
                </span>
                <button onClick={() => handleRemove(p.id)} className="text-gray-400 hover:text-red-500 p-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Team Tab ----
function TeamTab() {
  const { items: members, setItems, loading, error, load } = useList<TeamMember>('/team/members');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  useState(() => { load(); });

  async function handleInvite(e: FormEvent) {
    e.preventDefault();
    setInviteError(null);
    setInviting(true);
    try {
      const newMember = await api.post<TeamMember>('/team/invite', { email: inviteEmail, role: inviteRole });
      setItems(prev => [...prev, newMember]);
      setInviteEmail('');
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  }

  async function handleRemove(id: string) {
    if (!confirm('Remove this team member?')) return;
    await api.delete(`/team/members/${id}`);
    setItems(prev => prev.filter(m => m.id !== id));
  }

  async function handleRoleChange(id: string, role: string) {
    await api.put(`/team/members/${id}`, { role });
    setItems(prev => prev.map(m => m.id === id ? { ...m, role: role as TeamMember['role'] } : m));
  }

  const ROLE_COLORS: Record<string, string> = {
    owner: 'bg-purple-100 text-purple-700',
    admin: 'bg-indigo-100 text-indigo-700',
    member: 'bg-blue-100 text-blue-700',
    viewer: 'bg-gray-100 text-gray-600',
  };

  return (
    <div className="space-y-6">
      {/* Invite form */}
      <div className="border border-gray-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Invite Team Member</h3>
        {inviteError && <div className="mb-3 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{inviteError}</div>}
        <form onSubmit={handleInvite} className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-48">
            <label className="block text-xs font-medium text-gray-600 mb-1">Email address</label>
            <input
              type="email" required value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="colleague@company.com"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
            <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
              {ROLE_OPTIONS.map(r => <option key={r} value={r} className="capitalize">{r}</option>)}
            </select>
          </div>
          <button type="submit" disabled={inviting} className="bg-indigo-600 text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
            {inviting ? 'Sending...' : 'Send Invite'}
          </button>
        </form>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">{error}</div>}

      {loading ? (
        <div className="animate-pulse space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-14 bg-gray-100 rounded-lg" />)}</div>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
          {members.map(m => (
            <div key={m.id} className="px-5 py-4 flex items-center justify-between hover:bg-gray-50">
              <div>
                <p className="text-sm font-medium text-gray-900">{m.name || m.email}</p>
                <p className="text-xs text-gray-400 mt-0.5">{m.email} · {m.status === 'invited' ? 'Invite pending' : `Joined ${new Date(m.joined_at!).toLocaleDateString()}`}</p>
              </div>
              <div className="flex items-center gap-3">
                {m.role === 'owner' ? (
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${ROLE_COLORS.owner}`}>Owner</span>
                ) : (
                  <select
                    value={m.role}
                    onChange={e => handleRoleChange(m.id, e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none"
                  >
                    {ROLE_OPTIONS.map(r => <option key={r} value={r} className="capitalize">{r}</option>)}
                  </select>
                )}
                {m.role !== 'owner' && (
                  <button onClick={() => handleRemove(m.id)} className="text-gray-400 hover:text-red-500 p-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- API Keys Tab ----
function ApiKeysTab() {
  const { items: keys, setItems, loading, error, load } = useList<ApiKey>('/apikeys');
  const [keyName, setKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  useState(() => { load(); });

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setCreateError(null);
    setCreating(true);
    try {
      const result = await api.post<{ key: ApiKey; raw_key: string }>('/apikeys', { name: keyName });
      setItems(prev => [result.key, ...prev]);
      setNewKey(result.raw_key);
      setKeyName('');
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create key');
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(id: string) {
    if (!confirm('Revoke this API key? This cannot be undone.')) return;
    await api.delete(`/apikeys/${id}`);
    setItems(prev => prev.filter(k => k.id !== id));
  }

  return (
    <div className="space-y-6">
      {newKey && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm font-semibold text-green-800 mb-1">API key created — copy it now, it won't be shown again.</p>
          <div className="flex items-center gap-2">
            <code className="bg-white border border-green-200 rounded px-3 py-2 text-sm font-mono flex-1 text-green-900 break-all">{newKey}</code>
            <button onClick={() => { navigator.clipboard.writeText(newKey); }} className="flex-shrink-0 text-green-700 hover:text-green-900">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          </div>
          <button onClick={() => setNewKey(null)} className="mt-2 text-xs text-green-600 hover:underline">Dismiss</button>
        </div>
      )}

      <div className="border border-gray-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Create API Key</h3>
        {createError && <div className="mb-3 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{createError}</div>}
        <form onSubmit={handleCreate} className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Key name</label>
            <input
              required value={keyName} onChange={e => setKeyName(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g. CI/CD Pipeline"
            />
          </div>
          <button type="submit" disabled={creating} className="bg-indigo-600 text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
            {creating ? 'Creating...' : 'Create Key'}
          </button>
        </form>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">{error}</div>}

      {loading ? (
        <div className="animate-pulse space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-gray-100 rounded-lg" />)}</div>
      ) : keys.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-sm">No API keys. Create one above.</div>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
          {keys.map(k => (
            <div key={k.id} className="px-5 py-4 flex items-center justify-between hover:bg-gray-50">
              <div>
                <p className="text-sm font-medium text-gray-900">{k.name}</p>
                <div className="flex gap-3 text-xs text-gray-400 mt-0.5">
                  <span className="font-mono">{k.prefix}••••••••</span>
                  <span>Created {new Date(k.created_at).toLocaleDateString()}</span>
                  {k.last_used_at && <span>Last used {new Date(k.last_used_at).toLocaleDateString()}</span>}
                </div>
              </div>
              <button onClick={() => handleRevoke(k.id)} className="text-xs text-red-600 hover:text-red-800 font-medium border border-red-200 px-3 py-1 rounded">
                Revoke
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- GitHub Tab ----
function GitHubTab() {
  const { data: config } = useSecurityConfig();
  const cfg = config as Record<string, unknown>;
  const connected = cfg?.github_connected === true;
  const installationId = cfg?.github_installation_id as string | undefined;
  const connectedAt = cfg?.github_connected_at as string | undefined;
  const [disconnecting, setDisconnecting] = useState(false);

  async function handleDisconnect() {
    if (!confirm('Disconnect GitHub? Scans will stop working.')) return;
    setDisconnecting(true);
    try {
      await api.delete('/security/github/installation');
      window.location.reload();
    } catch {
      setDisconnecting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="border border-gray-200 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center flex-shrink-0">
            <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-gray-900">GitHub App</h3>
            {connected ? (
              <>
                <div className="flex items-center gap-2 mt-1">
                  <span className="w-2 h-2 bg-green-500 rounded-full" />
                  <span className="text-sm text-green-700 font-medium">Connected</span>
                </div>
                {installationId && <p className="text-xs text-gray-400 mt-1">Installation ID: {installationId}</p>}
                {connectedAt && <p className="text-xs text-gray-400">Connected {new Date(connectedAt).toLocaleDateString()}</p>}
                <div className="mt-4 flex gap-3">
                  <a
                    href={`https://github.com/apps/the-one-security/installations/${installationId ?? 'new'}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-800 border border-indigo-200 px-4 py-2 rounded-md"
                  >
                    Manage Repositories
                  </a>
                  <button
                    onClick={handleDisconnect} disabled={disconnecting}
                    className="text-sm font-medium text-red-600 hover:text-red-800 border border-red-200 px-4 py-2 rounded-md disabled:opacity-50"
                  >
                    {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-500 mt-1">Not connected. Install the GitHub App to enable scanning.</p>
                <a
                  href="https://github.com/apps/the-one-security/installations/new"
                  target="_blank" rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 bg-gray-900 text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-gray-800"
                >
                  Install GitHub App
                </a>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-700">
        <p className="font-medium mb-1">What the GitHub App accesses:</p>
        <ul className="space-y-0.5 text-blue-600">
          <li>• Read access to repository contents and metadata</li>
          <li>• Pull request read access for scan annotations</li>
          <li>• Webhook notifications on push events</li>
          <li>• No write access to code is ever requested</li>
        </ul>
      </div>
    </div>
  );
}

// ---- Notifications Tab ----
const SEVERITY_OPTIONS = ['critical', 'high', 'medium', 'low', 'info'];
const CATEGORY_OPTIONS = ['malware', 'brute_force', 'phishing', 'data_exfil', 'ransomware', 'insider_threat', 'vulnerability', 'compliance', 'policy_violation', 'other'];
const SOURCE_OPTIONS = ['siem', 'appsec', 'email_security', 'dark_web', 'shadow_ai', 'network', 'endpoint', 'grc', 'threat_intel'];

const EMPTY_RULE_FORM = {
  name: '',
  enabled: true,
  severity: [] as string[],
  categories: [] as string[],
  sources: [] as string[],
  email_enabled: false,
  email_recipients: '',
  slack_enabled: false,
  slack_webhook_url: '',
  slack_channel: '',
  psa_enabled: false,
  psa_board_id: '',
  psa_priority: 'medium',
  webhook_enabled: false,
  webhook_url: '',
};

type RuleForm = typeof EMPTY_RULE_FORM;

function ruleToForm(rule: NotificationRoutingRule): RuleForm {
  return {
    name: rule.name,
    enabled: rule.enabled,
    severity: rule.conditions.severity,
    categories: rule.conditions.categories,
    sources: rule.conditions.sources,
    email_enabled: rule.actions.email.enabled,
    email_recipients: rule.actions.email.recipients.join(', '),
    slack_enabled: rule.actions.slack.enabled,
    slack_webhook_url: rule.actions.slack.webhook_url,
    slack_channel: rule.actions.slack.channel,
    psa_enabled: rule.actions.psa_ticket.enabled,
    psa_board_id: rule.actions.psa_ticket.board_id,
    psa_priority: rule.actions.psa_ticket.priority,
    webhook_enabled: rule.actions.webhook.enabled,
    webhook_url: rule.actions.webhook.url,
  };
}

function formToPayload(form: RuleForm) {
  return {
    name: form.name,
    enabled: form.enabled,
    conditions: {
      severity: form.severity,
      categories: form.categories,
      sources: form.sources,
    },
    actions: {
      email: { enabled: form.email_enabled, recipients: form.email_recipients.split(',').map(s => s.trim()).filter(Boolean) },
      slack: { enabled: form.slack_enabled, webhook_url: form.slack_webhook_url, channel: form.slack_channel },
      psa_ticket: { enabled: form.psa_enabled, board_id: form.psa_board_id, priority: form.psa_priority },
      webhook: { enabled: form.webhook_enabled, url: form.webhook_url },
    },
  };
}

function MultiSelect({ options, selected, onChange, label }: { options: string[]; selected: string[]; onChange: (v: string[]) => void; label: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <div className="flex flex-wrap gap-1.5">
        {options.map(opt => {
          const active = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(active ? selected.filter(s => s !== opt) : [...selected, opt])}
              className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                active
                  ? 'bg-indigo-100 text-indigo-700 border-indigo-300'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
              }`}
            >
              {opt.replace(/_/g, ' ')}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NotificationsTab() {
  const [rules, setRules] = useState<NotificationRoutingRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<RuleForm>({ ...EMPTY_RULE_FORM });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    loadRules();
  }, []);

  async function loadRules() {
    setLoading(true);
    try {
      const res = await secopsApi.get<{ data: NotificationRoutingRule[] }>('/secops/notification-routing');
      setRules(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditId(null);
    setForm({ ...EMPTY_RULE_FORM });
    setFormError(null);
    setShowForm(true);
  }

  function openEdit(rule: NotificationRoutingRule) {
    setEditId(rule.id);
    setForm(ruleToForm(rule));
    setFormError(null);
    setShowForm(true);
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setSaving(true);
    try {
      const payload = formToPayload(form);
      if (editId) {
        const res = await secopsApi.put<{ data: NotificationRoutingRule }>(`/secops/notification-routing/${editId}`, payload);
        setRules(prev => prev.map(r => r.id === editId ? res.data : r));
      } else {
        const res = await secopsApi.post<{ data: NotificationRoutingRule }>('/secops/notification-routing', payload);
        setRules(prev => [res.data, ...prev]);
      }
      setShowForm(false);
      setEditId(null);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(rule: NotificationRoutingRule) {
    try {
      const res = await secopsApi.put<{ data: NotificationRoutingRule }>(`/secops/notification-routing/${rule.id}`, { enabled: !rule.enabled });
      setRules(prev => prev.map(r => r.id === rule.id ? res.data : r));
    } catch {
      // silent
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this routing rule?')) return;
    try {
      await secopsApi.delete(`/secops/notification-routing/${id}`);
      setRules(prev => prev.filter(r => r.id !== id));
    } catch {
      // silent
    }
  }

  function actionsSummary(rule: NotificationRoutingRule): string {
    const parts: string[] = [];
    if (rule.actions.email.enabled) parts.push('Email');
    if (rule.actions.slack.enabled) parts.push('Slack');
    if (rule.actions.psa_ticket.enabled) parts.push('PSA Ticket');
    if (rule.actions.webhook.enabled) parts.push('Webhook');
    return parts.length > 0 ? parts.join(', ') : 'No actions';
  }

  function conditionsSummary(rule: NotificationRoutingRule): string {
    const parts: string[] = [];
    if (rule.conditions.severity.length > 0) parts.push(rule.conditions.severity.join(', '));
    if (rule.conditions.categories.length > 0) parts.push(`${rule.conditions.categories.length} categories`);
    if (rule.conditions.sources.length > 0) parts.push(`${rule.conditions.sources.length} sources`);
    return parts.length > 0 ? parts.join(' · ') : 'All alerts';
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{rules.length} routing rule{rules.length !== 1 ? 's' : ''}</p>
        <button
          onClick={openCreate}
          className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Rule
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">{error}</div>}

      {showForm && (
        <div className="border border-gray-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">{editId ? 'Edit Rule' : 'New Routing Rule'}</h3>
          {formError && <div className="mb-3 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{formError}</div>}
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Rule Name *</label>
                <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g. Critical alerts to Slack" />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.enabled} onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                  Enabled
                </label>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4">
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">Conditions</h4>
              <div className="space-y-3">
                <MultiSelect options={SEVERITY_OPTIONS} selected={form.severity} onChange={v => setForm(f => ({ ...f, severity: v }))} label="Severity" />
                <MultiSelect options={CATEGORY_OPTIONS} selected={form.categories} onChange={v => setForm(f => ({ ...f, categories: v }))} label="Categories" />
                <MultiSelect options={SOURCE_OPTIONS} selected={form.sources} onChange={v => setForm(f => ({ ...f, sources: v }))} label="Sources" />
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4">
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">Actions</h4>
              <div className="space-y-4">
                {/* Email */}
                <div className="border border-gray-100 rounded-lg p-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <input type="checkbox" checked={form.email_enabled} onChange={e => setForm(f => ({ ...f, email_enabled: e.target.checked }))}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                    Email
                  </label>
                  {form.email_enabled && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Recipients (comma-separated)</label>
                      <input value={form.email_recipients} onChange={e => setForm(f => ({ ...f, email_recipients: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="security@company.com, alerts@company.com" />
                    </div>
                  )}
                </div>

                {/* Slack */}
                <div className="border border-gray-100 rounded-lg p-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <input type="checkbox" checked={form.slack_enabled} onChange={e => setForm(f => ({ ...f, slack_enabled: e.target.checked }))}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                    Slack
                  </label>
                  {form.slack_enabled && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Webhook URL</label>
                        <input type="url" value={form.slack_webhook_url} onChange={e => setForm(f => ({ ...f, slack_webhook_url: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="https://hooks.slack.com/..." />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Channel</label>
                        <input value={form.slack_channel} onChange={e => setForm(f => ({ ...f, slack_channel: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="#security-alerts" />
                      </div>
                    </div>
                  )}
                </div>

                {/* PSA Ticket */}
                <div className="border border-gray-100 rounded-lg p-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <input type="checkbox" checked={form.psa_enabled} onChange={e => setForm(f => ({ ...f, psa_enabled: e.target.checked }))}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                    PSA Ticket
                  </label>
                  {form.psa_enabled && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Board ID</label>
                        <input value={form.psa_board_id} onChange={e => setForm(f => ({ ...f, psa_board_id: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Board ID" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                        <select value={form.psa_priority} onChange={e => setForm(f => ({ ...f, psa_priority: e.target.value }))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
                          <option value="critical">Critical</option>
                          <option value="high">High</option>
                          <option value="medium">Medium</option>
                          <option value="low">Low</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                {/* Webhook */}
                <div className="border border-gray-100 rounded-lg p-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <input type="checkbox" checked={form.webhook_enabled} onChange={e => setForm(f => ({ ...f, webhook_enabled: e.target.checked }))}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                    Webhook
                  </label>
                  {form.webhook_enabled && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Webhook URL</label>
                      <input type="url" value={form.webhook_url} onChange={e => setForm(f => ({ ...f, webhook_url: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="https://..." />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => { setShowForm(false); setEditId(null); }} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
              <button type="submit" disabled={saving} className="bg-indigo-600 text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
                {saving ? 'Saving...' : editId ? 'Update Rule' : 'Create Rule'}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="animate-pulse space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-gray-100 rounded-lg" />)}</div>
      ) : rules.length === 0 ? (
        <div className="text-center py-10 text-gray-400 text-sm">No routing rules yet. Click "Add Rule" to get started.</div>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
          {rules.map(rule => (
            <div key={rule.id} className="px-5 py-4 flex items-center justify-between hover:bg-gray-50">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900">{rule.name}</p>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${rule.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {rule.enabled ? 'Active' : 'Disabled'}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5 truncate">
                  {conditionsSummary(rule)} &rarr; {actionsSummary(rule)}
                </p>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <button onClick={() => handleToggle(rule)} className="text-xs text-gray-500 hover:text-gray-700 border border-gray-200 px-2.5 py-1 rounded">
                  {rule.enabled ? 'Disable' : 'Enable'}
                </button>
                <button onClick={() => openEdit(rule)} className="text-xs text-indigo-600 hover:text-indigo-800 border border-indigo-200 px-2.5 py-1 rounded">
                  Edit
                </button>
                <button onClick={() => handleDelete(rule.id)} className="text-gray-400 hover:text-red-500 p-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Scans Tab ----
const SCAN_TYPE_OPTIONS = ['vulnerability', 'compliance', 'dast', 'network'] as const;
const FREQUENCY_OPTIONS = ['daily', 'weekly', 'monthly', 'custom'] as const;
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const EMPTY_SCHEDULE_FORM = {
  name: '',
  enabled: true,
  scan_type: 'vulnerability' as string,
  targets: '',
  frequency: 'daily' as string,
  cron_expression: '',
  day_of_week: 1,
  day_of_month: 1,
  time_utc: '02:00',
  notification_on_complete: false,
  notification_on_findings: true,
};

type ScheduleForm = typeof EMPTY_SCHEDULE_FORM;

function scheduleToForm(s: ScanSchedule): ScheduleForm {
  return {
    name: s.name,
    enabled: s.enabled,
    scan_type: s.scan_type,
    targets: s.targets.join(', '),
    frequency: s.frequency,
    cron_expression: s.cron_expression,
    day_of_week: s.day_of_week ?? 1,
    day_of_month: s.day_of_month ?? 1,
    time_utc: s.time_utc,
    notification_on_complete: s.notification_on_complete,
    notification_on_findings: s.notification_on_findings,
  };
}

// ---- Audit Log Tab ----
interface AuditEvent {
  id: string;
  actor_email?: string;
  action: string;
  action_category?: string;
  entity_type?: string;
  entity_id?: string;
  created_at: string;
}

function AuditTab() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [category, setCategory] = useState('');
  const LIMIT = 50;

  async function load(o: number, cat: string) {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(LIMIT), offset: String(o) });
      if (cat) params.set('category', cat);
      const data = await api.get<{ data: AuditEvent[]; pagination: { total: number } }>(`/audit-log?${params}`);
      setEvents(data.data);
      setTotal(data.pagination.total);
      setOffset(o);
    } catch { } finally { setLoading(false); }
  }

  useState(() => { load(0, ''); });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <select
          value={category}
          onChange={e => { setCategory(e.target.value); load(0, e.target.value); }}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="">All categories</option>
          <option value="auth">Auth</option>
          <option value="access">Access</option>
          <option value="data_create">Data Create</option>
          <option value="data_update">Data Update</option>
          <option value="data_delete">Data Delete</option>
          <option value="security">Security</option>
        </select>
        <button
          onClick={() => load(offset, category)}
          disabled={loading}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
        >
          {loading ? '...' : 'Refresh'}
        </button>
        <span className="text-xs text-gray-400 ml-auto">{total} events</span>
      </div>

      <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
        {loading ? (
          <div className="py-8 text-center text-sm text-gray-400">Loading…</div>
        ) : events.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-400">No audit events found</div>
        ) : (
          events.map(e => (
            <div key={e.id} className="px-5 py-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-mono text-indigo-700">{e.action}</span>
                <span className="text-xs text-gray-400">{new Date(e.created_at).toLocaleString()}</span>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">
                {e.actor_email ?? 'system'} · {e.entity_type ?? '—'}:{e.entity_id ?? '—'}
              </p>
            </div>
          ))
        )}
      </div>

      {total > LIMIT && (
        <div className="flex items-center justify-between">
          <button
            disabled={offset === 0}
            onClick={() => load(Math.max(0, offset - LIMIT), category)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-xs text-gray-400">{offset + 1}–{Math.min(offset + LIMIT, total)} of {total}</span>
          <button
            disabled={offset + LIMIT >= total}
            onClick={() => load(offset + LIMIT, category)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function ScansTab() {
  const [schedules, setSchedules] = useState<ScanSchedule[]>([]);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<ScheduleForm>({ ...EMPTY_SCHEDULE_FORM });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [schedRes, platRes] = await Promise.all([
        secopsApi.get<{ data: ScanSchedule[] }>('/secops/scan-schedules'),
        api.get<Platform[]>('/platforms'),
      ]);
      setSchedules(Array.isArray(schedRes.data) ? schedRes.data : []);
      setPlatforms(Array.isArray(platRes) ? platRes : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditId(null);
    setForm({ ...EMPTY_SCHEDULE_FORM });
    setFormError(null);
    setShowForm(true);
  }

  function openEdit(s: ScanSchedule) {
    setEditId(s.id);
    setForm(scheduleToForm(s));
    setFormError(null);
    setShowForm(true);
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        enabled: form.enabled,
        scan_type: form.scan_type,
        targets: form.targets.split(',').map(s => s.trim()).filter(Boolean),
        frequency: form.frequency,
        cron_expression: form.cron_expression,
        day_of_week: form.frequency === 'weekly' ? form.day_of_week : null,
        day_of_month: form.frequency === 'monthly' ? form.day_of_month : null,
        time_utc: form.time_utc,
        notification_on_complete: form.notification_on_complete,
        notification_on_findings: form.notification_on_findings,
      };
      if (editId) {
        const res = await secopsApi.put<{ data: ScanSchedule }>(`/secops/scan-schedules/${editId}`, payload);
        setSchedules(prev => prev.map(s => s.id === editId ? res.data : s));
      } else {
        const res = await secopsApi.post<{ data: ScanSchedule }>('/secops/scan-schedules', payload);
        setSchedules(prev => [res.data, ...prev]);
      }
      setShowForm(false);
      setEditId(null);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(s: ScanSchedule) {
    try {
      const res = await secopsApi.put<{ data: ScanSchedule }>(`/secops/scan-schedules/${s.id}`, { enabled: !s.enabled });
      setSchedules(prev => prev.map(x => x.id === s.id ? res.data : x));
    } catch {
      // silent
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this scan schedule?')) return;
    try {
      await secopsApi.delete(`/secops/scan-schedules/${id}`);
      setSchedules(prev => prev.filter(s => s.id !== id));
    } catch {
      // silent
    }
  }

  const SCAN_TYPE_LABELS: Record<string, string> = { vulnerability: 'Vulnerability', compliance: 'Compliance', dast: 'DAST', network: 'Network' };
  const FREQ_LABELS: Record<string, string> = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', custom: 'Custom' };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{schedules.length} schedule{schedules.length !== 1 ? 's' : ''}</p>
        <button
          onClick={openCreate}
          className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Schedule
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">{error}</div>}

      {showForm && (
        <div className="border border-gray-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">{editId ? 'Edit Schedule' : 'New Scan Schedule'}</h3>
          {formError && <div className="mb-3 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{formError}</div>}
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Schedule Name *</label>
                <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g. Nightly Vuln Scan" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Scan Type *</label>
                <select value={form.scan_type} onChange={e => setForm(f => ({ ...f, scan_type: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
                  {SCAN_TYPE_OPTIONS.map(t => <option key={t} value={t}>{SCAN_TYPE_LABELS[t] ?? t}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Targets (platform IDs or URLs, comma-separated)</label>
              {platforms.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {platforms.map(p => {
                    const selected = form.targets.split(',').map(s => s.trim()).includes(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          const current = form.targets.split(',').map(s => s.trim()).filter(Boolean);
                          const next = selected ? current.filter(t => t !== p.id) : [...current, p.id];
                          setForm(f => ({ ...f, targets: next.join(', ') }));
                        }}
                        className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                          selected
                            ? 'bg-indigo-100 text-indigo-700 border-indigo-300'
                            : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {p.name}
                      </button>
                    );
                  })}
                </div>
              )}
              <input value={form.targets} onChange={e => setForm(f => ({ ...f, targets: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="platform-id-1, https://example.com" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Frequency</label>
                <select value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
                  {FREQUENCY_OPTIONS.map(f => <option key={f} value={f}>{FREQ_LABELS[f] ?? f}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Time (UTC)</label>
                <input type="time" value={form.time_utc} onChange={e => setForm(f => ({ ...f, time_utc: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              {form.frequency === 'weekly' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Day of Week</label>
                  <select value={form.day_of_week} onChange={e => setForm(f => ({ ...f, day_of_week: Number(e.target.value) }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
                    {DAY_NAMES.map((name, i) => <option key={i} value={i}>{name}</option>)}
                  </select>
                </div>
              )}
              {form.frequency === 'monthly' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Day of Month</label>
                  <input type="number" min={1} max={31} value={form.day_of_month} onChange={e => setForm(f => ({ ...f, day_of_month: Number(e.target.value) }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
              )}
              {form.frequency === 'custom' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Cron Expression</label>
                  <input value={form.cron_expression} onChange={e => setForm(f => ({ ...f, cron_expression: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="0 2 * * 1-5" />
                </div>
              )}
            </div>

            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.notification_on_complete} onChange={e => setForm(f => ({ ...f, notification_on_complete: e.target.checked }))}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                Notify on completion
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.notification_on_findings} onChange={e => setForm(f => ({ ...f, notification_on_findings: e.target.checked }))}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                Notify on findings
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.enabled} onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                Enabled
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => { setShowForm(false); setEditId(null); }} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
              <button type="submit" disabled={saving} className="bg-indigo-600 text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
                {saving ? 'Saving...' : editId ? 'Update Schedule' : 'Create Schedule'}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="animate-pulse space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-gray-100 rounded-lg" />)}</div>
      ) : schedules.length === 0 ? (
        <div className="text-center py-10 text-gray-400 text-sm">No scan schedules yet. Click "Add Schedule" to get started.</div>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
          {schedules.map(s => (
            <div key={s.id} className="px-5 py-4 flex items-center justify-between hover:bg-gray-50">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900">{s.name}</p>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${s.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {s.enabled ? 'Active' : 'Disabled'}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  {SCAN_TYPE_LABELS[s.scan_type] ?? s.scan_type} · {FREQ_LABELS[s.frequency] ?? s.frequency} at {s.time_utc} UTC
                  {s.next_run_at && <> · Next: {new Date(s.next_run_at).toLocaleString()}</>}
                </p>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <button onClick={() => handleToggle(s)} className="text-xs text-gray-500 hover:text-gray-700 border border-gray-200 px-2.5 py-1 rounded">
                  {s.enabled ? 'Disable' : 'Enable'}
                </button>
                <button onClick={() => openEdit(s)} className="text-xs text-indigo-600 hover:text-indigo-800 border border-indigo-200 px-2.5 py-1 rounded">
                  Edit
                </button>
                <button onClick={() => handleDelete(s.id)} className="text-gray-400 hover:text-red-500 p-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Main ----
export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('platforms');

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Settings</h1>

      <div className="bg-white rounded-lg shadow">
        {/* Tab nav */}
        <div className="border-b border-gray-200 px-6">
          <nav className="flex gap-6" aria-label="Settings tabs">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab content */}
        <div className="p-6">
          {activeTab === 'platforms' && <PlatformsTab />}
          {activeTab === 'notifications' && <NotificationsTab />}
          {activeTab === 'scans' && <ScansTab />}
          {activeTab === 'team' && <TeamTab />}
          {activeTab === 'apikeys' && <ApiKeysTab />}
          {activeTab === 'audit' && <AuditTab />}
          {activeTab === 'github' && <GitHubTab />}
        </div>
      </div>
    </div>
  );
}
